/* ==========================================================================
   LP ANIMATIONS — Controlador de animações premium
   Loja Premium · Moda Masculina

   Funcionalidades:
   1. Scroll-triggered animations (IntersectionObserver)
   2. Stagger para grids (delay progressivo)
   3. Header glassmorphism on scroll
   4. Button ripple effect
   5. Scroll progress bar
   ========================================================================== */

(() => {
  'use strict';

  /* --------------------------------------------------------------------------
     1. SCROLL ANIMATIONS — IntersectionObserver
     -------------------------------------------------------------------------- */

  class ScrollAnimator {
    constructor() {
      this.observer = null;
      this.init();
    }

    init() {
      if (!('IntersectionObserver' in window)) {
        // Fallback: mostrar tudo imediatamente
        document.querySelectorAll('.animate-on-scroll').forEach(el => {
          el.classList.add('is-visible');
        });
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              // Adicionar stagger delay para itens em grid
              const parent = entry.target.closest('[data-stagger]');
              if (parent) {
                const siblings = [...parent.querySelectorAll('.animate-on-scroll')];
                const index = siblings.indexOf(entry.target);
                const delay = parseInt(parent.dataset.stagger || '80', 10);
                entry.target.style.transitionDelay = `${index * delay}ms`;
              }

              entry.target.classList.add('is-visible');
              this.observer.unobserve(entry.target);
            }
          });
        },
        {
          threshold: 0.08,
          rootMargin: '0px 0px -60px 0px'
        }
      );

      this.observeAll();

      // Re-observe após section rendering (Shopify AJAX)
      document.addEventListener('shopify:section:load', () => {
        this.observeAll();
      });
    }

    observeAll() {
      document.querySelectorAll('.animate-on-scroll:not(.is-visible)').forEach(el => {
        this.observer.observe(el);
      });
    }
  }

  /* --------------------------------------------------------------------------
     2. HEADER GLASSMORPHISM — Transparent → Glass on scroll
     -------------------------------------------------------------------------- */

  class HeaderGlass {
    constructor() {
      this.header = document.querySelector('[data-header-glass]');
      if (!this.header) return;

      this.scrollThreshold = parseInt(this.header.dataset.headerGlass || '80', 10);
      this.ticking = false;

      window.addEventListener('scroll', () => this.onScroll(), { passive: true });
      this.onScroll(); // Estado inicial
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;

      requestAnimationFrame(() => {
        const scrolled = window.scrollY > this.scrollThreshold;
        this.header.classList.toggle('header--glass', scrolled);
        this.header.classList.toggle('header--transparent', !scrolled);
        this.ticking = false;
      });
    }
  }

  /* --------------------------------------------------------------------------
     3. BUTTON RIPPLE EFFECT
     -------------------------------------------------------------------------- */

  class ButtonRipple {
    constructor() {
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('.btn--ripple');
        if (!btn) return;

        const rect = btn.getBoundingClientRect();
        const size = Math.max(rect.width, rect.height);
        const x = e.clientX - rect.left - size / 2;
        const y = e.clientY - rect.top - size / 2;

        const ripple = document.createElement('span');
        ripple.classList.add('ripple');
        ripple.style.width = ripple.style.height = `${size}px`;
        ripple.style.left = `${x}px`;
        ripple.style.top = `${y}px`;

        btn.appendChild(ripple);

        ripple.addEventListener('animationend', () => ripple.remove());
      });
    }
  }

  /* --------------------------------------------------------------------------
     4. SCROLL PROGRESS BAR
     -------------------------------------------------------------------------- */

  class ScrollProgress {
    constructor() {
      this.bar = document.querySelector('.scroll-progress');
      if (!this.bar) return;

      this.ticking = false;
      window.addEventListener('scroll', () => this.update(), { passive: true });
      this.update();
    }

    update() {
      if (this.ticking) return;
      this.ticking = true;

      requestAnimationFrame(() => {
        const scrollTop = window.scrollY;
        const docHeight = document.documentElement.scrollHeight - window.innerHeight;
        const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
        this.bar.style.width = `${progress}%`;
        this.ticking = false;
      });
    }
  }

  /* --------------------------------------------------------------------------
     5. COUNTER ANIMATION — Anima números de 0 até o valor final
     -------------------------------------------------------------------------- */

  class CounterAnimation {
    constructor() {
      if (!('IntersectionObserver' in window)) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.animate(entry.target);
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.5 }
      );

      document.querySelectorAll('[data-count-to]').forEach(el => {
        observer.observe(el);
      });
    }

    animate(el) {
      const target = parseInt(el.dataset.countTo, 10);
      const duration = parseInt(el.dataset.countDuration || '1000', 10);
      const start = 0;
      const startTime = performance.now();

      const step = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Ease out expo
        const eased = 1 - Math.pow(1 - progress, 3);
        const current = Math.round(start + (target - start) * eased);

        el.textContent = current.toLocaleString('pt-BR');

        if (progress < 1) {
          requestAnimationFrame(step);
        }
      };

      requestAnimationFrame(step);
    }
  }

  /* --------------------------------------------------------------------------
     6. LAZY IMAGE REVEAL — Smooth fade on load
     -------------------------------------------------------------------------- */

  class ImageReveal {
    constructor() {
      document.querySelectorAll('img[loading="lazy"]').forEach(img => {
        if (img.complete) return;

        img.style.opacity = '0';
        img.style.transition = `opacity var(--duration-normal, 300ms) ease`;

        img.addEventListener('load', () => {
          img.style.opacity = '1';
        }, { once: true });
      });
    }
  }

  /* --------------------------------------------------------------------------
     INIT — Tudo ativa no DOMContentLoaded
     -------------------------------------------------------------------------- */

  document.addEventListener('DOMContentLoaded', () => {
    new ScrollAnimator();
    new HeaderGlass();
    new ButtonRipple();
    new ScrollProgress();
    new CounterAnimation();
    new ImageReveal();
  });

})();
